import React from 'react';

const TermsOfUse = () => {
  const lastUpdatedDate = "May 29, 2025"; // REPLACE WITH ACTUAL LAST UPDATED DATE
  const effectiveDate = "May 29, 2025"; // REPLACE WITH ACTUAL EFFECTIVE DATE

  // Helper for consistent section styling
  const Section = ({ title, children, id }) => (
    <section id={id} className="mb-8 scroll-mt-20"> {/* scroll-mt for anchor links */}
      <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-300 dark:border-gray-700 pb-2">
        {title}
      </h2>
      <div className="space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
        {children}
      </div>
    </section>
  );

  // Table of Contents data
  const tocItems = [
    { id: "introduction", title: "1. Introduction & Acceptance of Terms" },
    { id: "accounts", title: "2. User Accounts" },
    { id: "use-of-service", title: "3. Use of the Service & User Conduct" },
    { id: "intellectual-property", title: "4. Intellectual Property Rights" },
    { id: "user-content", title: "5. User-Generated Content (If Applicable)" },
    { id: "termination", title: "6. Termination" },
    { id: "disclaimers", title: "7. Disclaimers of Warranties" },
    { id: "limitation-liability", title: "8. Limitation of Liability" },
    { id: "indemnification", title: "9. Indemnification" },
    { id: "third-party-links", title: "10. Third-Party Links & Services" },
    { id: "governing-law", title: "11. Governing Law & Dispute Resolution" },
    { id: "changes-to-terms", title: "12. Changes to These Terms" },
    { id: "miscellaneous", title: "13. Miscellaneous" },
    { id: "contact-us", title: "14. Contact Information" },
  ];

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <header className="mb-10 text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white">
            Terms of Use for <span className="text-blue-600 dark:text-blue-400">TabDivider</span>
          </h1>
          <p className="mt-4 text-md sm:text-lg text-gray-600 dark:text-gray-400">
            Last Updated: {lastUpdatedDate}
          </p>
          <p className="mt-1 text-md sm:text-lg text-gray-600 dark:text-gray-400">
            Effective Date: {effectiveDate}
          </p>
        </header>

        {/* Optional: Table of Contents for long documents */}
        <nav className="mb-10 p-6 bg-white dark:bg-gray-800 shadow-lg rounded-lg">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3">Table of Contents</h2>
          <ul className="space-y-1">
            {tocItems.map(item => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className="text-blue-600 dark:text-blue-400 hover:underline text-sm sm:text-base"
                >
                  {item.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg p-6 sm:p-8 lg:p-10">
          <Section title="1. Introduction & Acceptance of Terms" id="introduction">
            <p>
              Welcome to TabDivider ("us," "we," or "our"). These Terms of Use ("Terms") govern your access to and use of our website, <a href="https://tabdivider.com" className="text-blue-600 dark:text-blue-400 hover:underline">tabdivider.com</a> (the "Site"), and our bill splitting and shared expense management services (collectively, the "Service").
            </p>
            <p>
              Please read these Terms carefully before using the Service. By accessing or using the Service, you agree to be bound by these Terms and our Privacy Policy [Link to Privacy Policy]. If you disagree with any part of the terms, then you may not access the Service.
            </p>
            <p>
              These Terms apply to all visitors, users, and others who access or use the Service. Your access to and use of the Service is conditioned upon your acceptance of and compliance with these Terms.
            </p>
          </Section>

          <Section title="2. User Accounts" id="accounts">
            <p>
              To use certain features of our Service, such as saving and sharing bills, you may be required to create an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
            </p>
            <p>
              You are responsible for safeguarding the password that you use to access the Service and for any activities or actions under your password, whether your password is with our Service or a third-party service (e.g., Supabase authentication).
            </p>
            <p>
              You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
            </p>
            <p>
              We reserve the right to suspend or terminate your account if any information provided during the registration process or thereafter proves to be inaccurate, not current, or incomplete.
            </p>
          </Section>

          <Section title="3. Use of the Service & User Conduct" id="use-of-service">
            <p>
              You agree to use the Service only for lawful purposes and in accordance with these Terms. You are solely responsible for your conduct and any data, text, information, screen names, graphics, photos, profiles, audio and video clips, links ("User Content") that you submit, post, and display on the Service.
            </p>
            <p>You agree not to use the Service:</p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>In any way that violates any applicable national or international law or regulation.</li>
              <li>For the purpose of exploiting, harming, or attempting to exploit or harm minors in any way.</li>
              <li>To transmit, or procure the sending of, any advertising or promotional material, including any "junk mail," "chain letter," "spam," or any other similar solicitation.</li>
              <li>To impersonate or attempt to impersonate TabDivider, a TabDivider employee, another user, or any other person or entity.</li>
              <li>To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Service, or which, as determined by us, may harm TabDivider or users of the Service or expose them to liability.</li>
              <li>To upload or transmit viruses, Trojan horses, worms, time-bombs, keystroke loggers, spyware, adware, or any other harmful programs or similar computer code designed to adversely affect the operation of any computer software or hardware.</li>
            </ul>
          </Section>

          <Section title="4. Intellectual Property Rights" id="intellectual-property">
            <p>
              The Service and its original content (excluding User Content, if applicable), features, and functionality are and will remain the exclusive property of TabDivider and its licensors. The Service is protected by copyright, trademark, and other laws of both [Your Country/Jurisdiction] and foreign countries.
            </p>
            <p>
              Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of TabDivider.
            </p>
            <p>
              You are granted a limited, non-exclusive, non-transferable, revocable license to access and use the Service strictly in accordance with these Terms for your personal, non-commercial use.
            </p>
          </Section>

          <Section title="5. User-Generated Content (If Applicable)" id="user-content">
            <p>
              If our Service allows you to post, link, store, share, and otherwise make available certain information, text, graphics, videos, or other material ("User Content"), you are responsible for the User Content that you post to the Service, including its legality, reliability, and appropriateness.
            </p>
            <p>
              By posting User Content to the Service, you grant us the right and license to use, modify, publicly perform, publicly display, reproduce, and distribute such User Content on and through the Service. You retain any and all of your rights to any User Content you submit, post, or display on or through the Service and you are responsible for protecting those rights.
            </p>
            <p>
              You represent and warrant that: (i) the User Content is yours (you own it) or you have the right to use it and grant us the rights and license as provided in these Terms, and (ii) the posting of your User Content on or through the Service does not violate the privacy rights, publicity rights, copyrights, contract rights, or any other rights of any person.
            </p>
            <p>
              We reserve the right to terminate the account of anyone found to be infringing on a copyright.
            </p>
          </Section>

          <Section title="6. Termination" id="termination">
            <p>
              We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms.
            </p>
            <p>
              If you wish to terminate your account, you may simply discontinue using the Service or follow any account deletion procedures provided within the Service.
            </p>
            <p>
              All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
            </p>
          </Section>

          <Section title="7. Disclaimers of Warranties" id="disclaimers">
            <p>
              THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. TABDIVIDER MAKES NO REPRESENTATIONS OR WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, AS TO THE OPERATION OF THEIR SERVICES, OR THE INFORMATION, CONTENT OR MATERIALS INCLUDED THEREIN. YOU EXPRESSLY AGREE THAT YOUR USE OF THESE SERVICES, THEIR CONTENT, AND ANY SERVICES OR ITEMS OBTAINED FROM US IS AT YOUR SOLE RISK.
            </p>
            <p>
              NEITHER TABDIVIDER NOR ANY PERSON ASSOCIATED WITH TABDIVIDER MAKES ANY WARRANTY OR REPRESENTATION WITH RESPECT TO THE COMPLETENESS, SECURITY, RELIABILITY, QUALITY, ACCURACY, OR AVAILABILITY OF THE SERVICES. WITHOUT LIMITING THE FOREGOING, NEITHER TABDIVIDER NOR ANYONE ASSOCIATED WITH TABDIVIDER REPRESENTS OR WARRANTS THAT THE SERVICES, THEIR CONTENT, OR ANY SERVICES OR ITEMS OBTAINED THROUGH THE SERVICES WILL BE ACCURATE, RELIABLE, ERROR-FREE, OR UNINTERRUPTED, THAT DEFECTS WILL BE CORRECTED, THAT THE SERVICES OR THE SERVER THAT MAKES IT AVAILABLE ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS OR THAT THE SERVICES OR ANY SERVICES OR ITEMS OBTAINED THROUGH THE SERVICES WILL OTHERWISE MEET YOUR NEEDS OR EXPECTATIONS.
            </p>
            <p>
              TABDIVIDER HEREBY DISCLAIMS ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING BUT NOT LIMITED TO ANY WARRANTIES OF MERCHANTABILITY, NON-INFRINGEMENT, AND FITNESS FOR PARTICULAR PURPOSE.
            </p>
            <p>
              THE FOREGOING DOES NOT AFFECT ANY WARRANTIES WHICH CANNOT BE EXCLUDED OR LIMITED UNDER APPLICABLE LAW.
            </p>
          </Section>

          <Section title="8. Limitation of Liability" id="limitation-liability">
            <p>
              EXCEPT AS PROHIBITED BY LAW, YOU WILL HOLD US AND OUR OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS HARMLESS FOR ANY INDIRECT, PUNITIVE, SPECIAL, INCIDENTAL, OR CONSEQUENTIAL DAMAGE, HOWEVER IT ARISES (INCLUDING ATTORNEYS' FEES AND ALL RELATED COSTS AND EXPENSES OF LITIGATION AND ARBITRATION, OR AT TRIAL OR ON APPEAL, IF ANY, WHETHER OR NOT LITIGATION OR ARBITRATION IS INSTITUTED), WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE, OR OTHER TORTIOUS ACTION, OR ARISING OUT OF OR IN CONNECTION WITH THIS AGREEMENT, INCLUDING WITHOUT LIMITATION ANY CLAIM FOR PERSONAL INJURY OR PROPERTY DAMAGE, ARISING FROM THIS AGREEMENT AND ANY VIOLATION BY YOU OF ANY FEDERAL, STATE, OR LOCAL LAWS, STATUTES, RULES, OR REGULATIONS, EVEN IF TABDIVIDER HAS BEEN PREVIOUSLY ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
            </p>
            <p>
              EXCEPT AS PROHIBITED BY LAW, IF THERE IS LIABILITY FOUND ON THE PART OF TABDIVIDER, IT WILL BE LIMITED TO THE AMOUNT PAID FOR THE PRODUCTS AND/OR SERVICES, AND UNDER NO CIRCUMSTANCES WILL THERE BE CONSEQUENTIAL OR PUNITIVE DAMAGES. SOME STATES DO NOT ALLOW THE EXCLUSION OR LIMITATION OF PUNITIVE, INCIDENTAL OR CONSEQUENTIAL DAMAGES, SO THE PRIOR LIMITATION OR EXCLUSION MAY NOT APPLY TO YOU.
            </p>
          </Section>

           <Section title="9. Indemnification" id="indemnification">
            <p>
              You agree to defend, indemnify, and hold harmless TabDivider, its affiliates, licensors, and service providers, and its and their respective officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your violation of these Terms of Use or your use of the Service, including, but not limited to, your User Content, any use of the Service's content, services, and products other than as expressly authorized in these Terms of Use or your use of any information obtained from the Service.
            </p>
          </Section>

          <Section title="10. Third-Party Links & Services" id="third-party-links">
            <p>
              Our Service may contain links to third-party web sites or services that are not owned or controlled by TabDivider (e.g., Supabase for authentication).
            </p>
            <p>
              TabDivider has no control over, and assumes no responsibility for, the content, privacy policies, or practices of any third-party web sites or services. You further acknowledge and agree that TabDivider shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with the use of or reliance on any such content, goods or services available on or through any such web sites or services.
            </p>
            <p>
              We strongly advise you to read the terms and conditions and privacy policies of any third-party web sites or services that you visit.
            </p>
          </Section>

          <Section title="11. Governing Law & Dispute Resolution" id="governing-law">
            <p>
              These Terms shall be governed and construed in accordance with the laws of [Your State/Country, e.g., "the State of California, United States"], without regard to its conflict of law provisions.
            </p>
            <p>
              Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect. These Terms constitute the entire agreement between us regarding our Service, and supersede and replace any prior agreements we might have had between us regarding the Service.
            </p>
            <p>
              [Optional: Add a clause about dispute resolution, e.g., arbitration or mediation, if desired and legally appropriate for your jurisdiction.]
            </p>
          </Section>

          <Section title="12. Changes to These Terms" id="changes-to-terms">
            <p>
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least [e.g., "30 days'"] notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
            </p>
            <p>
              By continuing to access or use our Service after any revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, you are no longer authorized to use the Service. We will update the "Last Updated" date at the top of these Terms of Use.
            </p>
          </Section>

          <Section title="13. Miscellaneous" id="miscellaneous">
            <p>
              <strong>Severability:</strong> If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that these Terms will otherwise remain in full force and effect and enforceable.
            </p>
            <p>
              <strong>Waiver:</strong> No waiver of any term of these Terms shall be deemed a further or continuing waiver of such term or any other term, and TabDivider's failure to assert any right or provision under these Terms shall not constitute a waiver of such right or provision.
            </p>
            <p>
              <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy [Link to Privacy Policy], constitute the entire agreement between you and TabDivider concerning the Service and supersede all prior or contemporaneous communications and proposals (whether oral, written, or electronic) between you and TabDivider with respect to the Service.
            </p>
          </Section>

          <Section title="14. Contact Information" id="contact-us">
            <p>
              If you have any questions about these Terms of Use, please contact us:
            </p>
            <p className="mt-2">
              TabDivider<br />
              Email: <a href="mailto:legal@tabdivider.com" className="text-blue-600 dark:text-blue-400 hover:underline">legal@tabdivider.com</a><br />
              {/* Add physical address if applicable/required */}
              {/* [Your Company Address Line 1] */}
              {/* [Your Company Address Line 2] */}
              {/* [City, State, Zip Code] */}
            </p>
          </Section>

          <div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700 text-center text-sm text-gray-500 dark:text-gray-400">
            <p>
              By using TabDivider, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;